import { useState, useEffect, useMemo } from "react";
import Service from "../service";
import { getRoles } from "../util/getRoles";

/**
 * Data source for the "Reporting Manager" filter on the Admin approval screens.
 *
 * Only Admin gets the filter: every other role is already scoped to its own team
 * server side, so the list is not fetched for them at all. The endpoint returns
 * the employees who are somebody's manager right now, whatever role they hold -
 * an Admin who manages people directly is still a reporting manager - so the
 * picker matches the "Reporting manager" column on these listings exactly.
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] fetch on mount. Pass false to hold the
 *   request back until the picker is actually needed; it runs once it flips to true.
 */
export default function useReportingManagerList({ enabled = true } = {}) {
  // Sub-Admin also sees company-wide request lists, so it gets the filter too.
  // The filter only narrows; the server's own scope still applies.
  const isAdmin = getRoles(["Admin", "Sub-Admin"]);
  const [reportingManagerList, setReportingManagerList] = useState([]);

  useEffect(() => {
    if (!isAdmin || !enabled) return undefined;

    let isCurrent = true;
    const fetchReportingManagers = async () => {
      try {
        const response = await Service.makeAPICall({
          methodName: Service.postMethod,
          api_url: Service.empDropdownList,
          body: {
            branch: "none",
            only_reporting_managers: true,
          },
        });
        if (!isCurrent) return;
        setReportingManagerList(
          Array.isArray(response?.data?.data) ? response.data.data : []
        );
      } catch (error) {
        if (!isCurrent) return;
        setReportingManagerList([]);
        console.error("Failed to fetch reporting managers", error);
      }
    };

    fetchReportingManagers();
    return () => {
      isCurrent = false;
    };
  }, [isAdmin, enabled]);

  const reportingManagerOptions = useMemo(
    () =>
      reportingManagerList.map((manager) => ({
        label:
          manager?.full_name ||
          `${manager?.first_name || ""} ${manager?.last_name || ""}`.trim(),
        value: manager?._id,
      })),
    [reportingManagerList]
  );

  return { isAdmin, reportingManagerList, reportingManagerOptions };
}
